import jsPDF from 'jspdf';

/**
 * Generates a PDF report for CXR-Multiclass AI results
 * @param {Object} patientData - Patient information
 * @param {string} annotatedImageBase64 - Base64 encoded annotated image (or null to use original)
 * @param {string} originalImageBase64 - Base64 encoded original image (fallback)
 * @param {Object} groupedBoxes - Grouped boxes with Explanation and Color
 * @param {string} adminName - Admin name for storage path
 * @returns {Promise<Blob>} - PDF blob
 */
export const generateCXRMulticlassReport = async (patientData, annotatedImageBase64, originalImageBase64, groupedBoxes) => {
  try {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    let yPosition = margin;

    // Title
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.text('X-ray Report', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 10;

    // Date
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    const currentDate = new Date().toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    pdf.text(`Date: ${currentDate}`, margin, yPosition);
    yPosition += 10;

    // Patient Information - Two columns
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    
    // Left column: Patient Name and Gender
    pdf.text(`Patient Name: ${patientData.name || 'N/A'}`, margin, yPosition);
    yPosition += 6;
    pdf.text(`Gender: ${patientData.gender || 'N/A'}`, margin, yPosition);
    
    // Right column: Patient ID and Age
    yPosition -= 6; // Reset to align with Patient Name
    pdf.text(`Patient ID: ${patientData.id || 'N/A'}`, pageWidth / 2 + 20, yPosition);
    yPosition += 6;
    pdf.text(`Age: ${patientData.age || 'N/A'}`, pageWidth / 2 + 20, yPosition);
    yPosition += 10;

    // Horizontal separator line
    pdf.setLineWidth(0.5);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 8;

    // AI Result Image Section
    pdf.setFont('helvetica', 'bold');
    pdf.text('AI Result Image', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 8;

    // Add image (annotated or original)
    const imageToUse = annotatedImageBase64 || originalImageBase64;
    if (imageToUse) {
      try {
        // Add 'data:image/png;base64,' prefix if not present
        const imageData = imageToUse.startsWith('data:') ? imageToUse : `data:image/png;base64,${imageToUse}`;
        
        // Calculate image dimensions to fit in PDF
        const maxImageWidth = pageWidth - (2 * margin);
        const maxImageHeight = 120; // Fixed height for consistency
        
        pdf.addImage(imageData, 'PNG', margin, yPosition, maxImageWidth, maxImageHeight);
        yPosition += maxImageHeight + 10;
      } catch (error) {
        console.error('Error adding image to PDF:', error);
        pdf.setFont('helvetica', 'italic');
        pdf.text('(Image could not be loaded)', pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 10;
      }
    } else {
      pdf.setFont('helvetica', 'italic');
      pdf.text('(No image available)', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;
    }

    // Findings Section
    pdf.setFont('helvetica', 'bold');
    pdf.text('Findings :', margin, yPosition);
    yPosition += 6;

    // Color mapping for boxes
    const colorMap = {
      'red': [255, 0, 0],
      'blue': [0, 0, 255],
      'green': [0, 255, 0],
      'yellow': [255, 255, 0],
      'purple': [128, 0, 128],
      'orange': [255, 165, 0],
      'cyan': [0, 255, 255],
      'magenta': [255, 0, 255]
    };

    // Display findings with color boxes
    if (groupedBoxes && Object.keys(groupedBoxes).length > 0) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      
      Object.values(groupedBoxes).forEach((finding) => {
        // Check if we need a new page
        if (yPosition > pageHeight - 30) {
          pdf.addPage();
          yPosition = margin;
        }

        if (finding.Color) {
          // Draw colored box
          const boxSize = 4;
          // Ensure Color is a string before calling toLowerCase
          const colorKey = typeof finding.Color === 'string' ? finding.Color.toLowerCase() : String(finding.Color).toLowerCase();
          const rgb = colorMap[colorKey] || [0, 0, 0];
          pdf.setFillColor(rgb[0], rgb[1], rgb[2]);
          pdf.rect(margin, yPosition - 2, boxSize, boxSize, 'F');
          
          // Add finding text next to the box
          const textX = margin + boxSize + 2;
          const splitText = pdf.splitTextToSize(finding.Explanation, pageWidth - textX - margin);
          pdf.text(splitText, textX, yPosition);
          yPosition += (splitText.length * 5) + 2;
        } else {
          // Draw bullet point for findings without color
          pdf.setFillColor(0, 0, 0);
          pdf.circle(margin + 1.5, yPosition - 1.5, 0.8, 'F');
          
          // Add finding text next to the bullet
          const textX = margin + 4;
          const splitText = pdf.splitTextToSize(finding.Explanation, pageWidth - textX - margin);
          pdf.text(splitText, textX, yPosition);
          yPosition += (splitText.length * 5) + 2;
        }
      });
    } else {
      pdf.setFont('helvetica', 'italic');
      pdf.text('No findings available', margin, yPosition);
    }

    // Add footer at the bottom of the page
    const footerY = pageHeight - 15;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.text('ImagiXAI by Easiofy Solutions Pvt. Ltd.', pageWidth / 2, footerY, { align: 'center' });

    // Generate PDF blob
    const pdfBlob = pdf.output('blob');
    return pdfBlob;
  } catch (error) {
    console.error('Error generating PDF report:', error);
    throw error;
  }
};
